import { Button } from "@/components/ui/button.tsx";
import { confirmationToast, errorToast } from "@/components/ui/toast.tsx";
import {
  describeInviteFailure,
  inviteEventParticipant,
  participantOkthxStatusQueryOptions,
} from "@/shared/eventParticipantInvites.ts";
import type {
  EventParticipantOkthxStatusResponse,
  ParticipantOkthxStatus,
} from "@/types/events.ts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

/**
 * A participant row's Invite button, or the "Invited" note once they have
 * been. Renders nothing for members and for anyone the server gave no status.
 *
 * Each row subscribes to its own entry of the event's status query, so every
 * row shares one fetch and an invite re-renders only the row it changed.
 */
export const ParticipantInviteAction = ({ eventId, email }: { eventId: string; email: string }) => {
  const queryClient = useQueryClient();
  const statusQuery = participantOkthxStatusQueryOptions(eventId);
  const key = email.toLowerCase();
  const { data: status } = useQuery({ ...statusQuery, select: (data) => data.statuses[key] });

  const setStatus = async (next: ParticipantOkthxStatus) => {
    // A refetch already in flight predates this invite and would put the button back.
    await queryClient.cancelQueries({ queryKey: statusQuery.queryKey });
    queryClient.setQueryData<EventParticipantOkthxStatusResponse>(
      statusQuery.queryKey,
      (old) => old && { statuses: { ...old.statuses, [key]: next } },
    );
  };

  const mutation = useMutation({
    mutationFn: () => inviteEventParticipant(eventId, { email }),
    onSuccess: async (data) => {
      await setStatus("invited");
      confirmationToast(data.status === "alreadyInvited" ? "Already invited" : "Invite sent");
    },
    onError: async (error) => {
      const failure = describeInviteFailure(error);
      if (failure.alreadyMember) {
        await setStatus("member");
        confirmationToast(failure.message);
        return;
      }
      errorToast(failure.message);
    },
  });

  if (status === "invited") {
    return <span className="text-xs text-muted-foreground shrink-0">Invited</span>;
  }
  if (status !== "invitable") return null;
  return (
    <Button
      // The row sits inside the event panel's form; a submit would save the event.
      type="button"
      variant="ghost"
      size="2xs"
      data-statsig-name="event-invite-participant"
      aria-label={`Invite ${email} to okthx`}
      disabled={mutation.isPending}
      onClick={() => mutation.mutate()}
      className="shrink-0"
    >
      {mutation.isPending ? <Loader2 className="size-3 animate-spin" /> : "Invite"}
    </Button>
  );
};
